/**
 * Renders provider-authored HTML — Ivanti's rich text, screenshots and all.
 *
 * WHY THIS EXISTS
 * ---------------
 * `Incident.Symptom` is not plain text. Ivanti's Self Service portal writes a
 * rich text editor's output into it, images included as base64 `data:` URIs —
 * one real UAT incident is 333,259 characters, almost all of it one PNG. Until
 * the backend started tagging those values `format: "HTML"`, this app escaped
 * them, so a ticket raised through Ivanti's own portal displayed
 * `<p><span style="font-size: 11pt;">` followed by a third of a megabyte of
 * base64 as visible text.
 *
 * WHY THERE IS NO `dangerouslySetInnerHTML` HERE
 * ----------------------------------------------
 * The text is written by whoever raised or worked the ticket and arrives from
 * another system, so it is untrusted even though `backend/richtext.py` has
 * already run it through `nh3`. The obvious client-side shape — a second
 * sanitiser library plus `dangerouslySetInnerHTML` — keeps an HTML sink in the
 * component and makes safety depend on that library staying correctly
 * configured. This module has no sink at all:
 *
 *   1. The markup is parsed by `DOMParser`, which produces an **inert**
 *      document: no browsing context, so no script runs, no `<img>` fetches
 *      and no handler fires during parsing.
 *   2. The inert tree is then *copied* into React elements. Only a tag in
 *      {@link RENDERED_TAGS} ever becomes an element, only the attributes
 *      named in this file are carried across, and `href`/`src` must survive a
 *      scheme check. Nothing else is transcribed — an `onerror=` or a `style`
 *      is not "stripped", it is simply never read (a grey `color` only
 *      chooses a design-token class, see {@link mutedClass}).
 *
 * The enlarge-a-screenshot dialog sits *after* that copy, not inside it:
 * {@link TicketImage} is handed a `src` and an `alt` that already passed the
 * checks below, and never sees the inert DOM. It adds a control, not a sink.
 *
 * So the failure mode of a bug in here is that content goes *missing*, never
 * that it executes. That is also why it does not have to be kept aligned with
 * the server's allowlist: an unknown tag is unwrapped to its children (its
 * text survives), a hostile one is in {@link DROPPED_TAGS}, and neither
 * decision depends on what `nh3` happened to keep.
 *
 * WHY NOT A LIBRARY
 * -----------------
 * Same answer as `markdown.tsx`: DOMPurify would be a dependency whose whole
 * job is making `dangerouslySetInnerHTML` survivable, and it has to be
 * *configured* to keep the `data:` screenshots the server deliberately kept.
 * Not adding the sink is cheaper than adding the guard for it.
 */

import { Icon, Minimize2, ZoomIn } from "@norican/icons";
import { Button, cn, Dialog } from "@norican/react";
import { createElement, Fragment, type ReactNode, useCallback, useMemo, useState } from "react";

import { useT } from "@/i18n";

import { IMAGE_PLACEHOLDER, safeHref, safeImageSrc } from "./rich-text-urls";

// ─── The allowlists ──────────────────────────────────────────────────────

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

/**
 * Tags rendered as themselves, with no attributes carried over.
 *
 * Headings are the exception: rich text pasted into a ticket routinely starts
 * at `h1`, and this content always sits under the page `<h1>` and a section
 * `<h2>`, so levels are demoted the same way `markdown.tsx` demotes them.
 * Getting this wrong is the usual way a page breaks screen-reader heading
 * navigation.
 */
const RENDERED_TAGS: Readonly<Record<string, string>> = {
  p: "p",
  br: "br",
  div: "div",
  span: "span",
  b: "b",
  strong: "strong",
  i: "i",
  em: "em",
  u: "u",
  s: "s",
  sub: "sub",
  sup: "sup",
  ul: "ul",
  ol: "ol",
  li: "li",
  h1: "h3",
  h2: "h4",
  h3: "h5",
  h4: "h6",
  h5: "h6",
  h6: "h6",
  blockquote: "blockquote",
  pre: "pre",
  code: "code",
  table: "table",
  thead: "thead",
  tbody: "tbody",
  tr: "tr",
  th: "th",
  td: "td",
  a: "a",
  img: "img",
  hr: "hr",
};

/**
 * Tags dropped with their whole subtree.
 *
 * An unknown tag is unwrapped rather than dropped, so that the words inside it
 * survive a future backend change. That is the wrong default for anything that
 * can act, load or capture, so those are named here instead — this list is the
 * only place "unwrap it" is overruled.
 */
const DROPPED_TAGS: ReadonlySet<string> = new Set([
  "applet",
  "audio",
  "base",
  "button",
  "canvas",
  "dialog",
  "embed",
  "form",
  "frame",
  "frameset",
  "iframe",
  "input",
  "link",
  "math",
  "meta",
  "noscript",
  "object",
  "option",
  "portal",
  "script",
  "select",
  "slot",
  "source",
  "style",
  "svg",
  "template",
  "textarea",
  "track",
  "video",
]);

/** Tags that must be rendered without children. */
const VOID_TAGS: ReadonlySet<string> = new Set(["br", "hr", "img"]);

/** Guards against a pathological nesting depth turning into a stack overflow. */
const MAX_DEPTH = 32;

/** A positive integer attribute, or nothing. Never a string passed through. */
function positiveInt(element: Element, name: string): number | undefined {
  const raw = element.getAttribute(name);
  if (raw === null) {
    return undefined;
  }
  const value = Number.parseInt(raw, 10);
  return Number.isSafeInteger(value) && value > 0 ? value : undefined;
}

/**
 * A grey inline colour (`style="color:#555555"`), the one style the backend
 * keeps (`richtext.grey_style`): the HelpDesk writes its AI summary and the
 * assistant's lines grey in Ivanti (13 Oct). Shown as muted text through the
 * design token; the style itself is never copied.
 */
const GREY_STYLE = /^color:#(?:([0-9a-f])\1\1|([0-9a-f]{2})\2\2)$/;

function mutedClass(element: Element): string | undefined {
  const style = element.getAttribute("style");
  return style !== null && GREY_STYLE.test(style.trim().toLowerCase())
    ? "text-fg-muted"
    : undefined;
}

// ─── The screenshot viewer ───────────────────────────────────────────────

/**
 * The tallest an inline image is drawn: `max-h-container-xs`, i.e. 20rem.
 *
 * It is the number the enlarge affordance turns on, because it is the number
 * that makes enlarging worth offering. Anything below it is already on screen
 * whole — an inline icon, a signature logo, a status glyph — and opening a
 * dialog over it shows the reader exactly what they were already looking at.
 * Keep this in step with the `[&_img]` rule in {@link RICH_TEXT_CLASSES}.
 */
const INLINE_IMAGE_CAP_PX = 320;

/** What we know about an image, which is nothing until the browser has it. */
type ImageState =
  | { kind: "pending" }
  | { kind: "failed" }
  | { kind: "loaded"; width: number; height: number };

interface TicketImageProps {
  /** Already through {@link safeImageSrc}. */
  src: string;
  alt: string;
  width?: number;
  height?: number;
}

/**
 * An embedded screenshot, enlargeable once it is worth enlarging.
 *
 * WHY THE AFFORDANCE WAITS FOR THE LOAD
 * -------------------------------------
 * These images are the reason the ticket was raised — an ERP error dialog, a
 * stack trace, a licence warning — and the words in them are the payload. At
 * the inline cap they are decoration; full size they are readable. So they get
 * a real control rather than a hover trick.
 *
 * But the control is only rendered once the browser reports a decoded image
 * (`naturalWidth > 0`), which buys two requirements at once:
 *
 *   - a `src` that 404s, or a `data:` URI Ivanti truncated, leaves a broken
 *     image and nothing else — never a focusable, clickable region over a
 *     picture that is not there;
 *   - the natural size is known, so a 16px inline icon is not given a "view
 *     full size" button that would show it at 16px.
 *
 * The cost is that the `<img>` is re-created when it moves inside the trigger.
 * For the case this module exists for — a base64 `data:` URI already in
 * memory — that is a re-decode, not a re-fetch.
 *
 * NOTHING HERE READS PROVIDER MARKUP
 * ----------------------------------
 * `src` and `alt` arrive as already-checked values from {@link renderNode};
 * this component never touches the inert DOM, so it adds no new path from a
 * ticket author's string to the page.
 */
function TicketImage({ src, alt, width, height }: TicketImageProps) {
  const { t } = useT();
  const [state, setState] = useState<ImageState>({ kind: "pending" });
  /** `false` fits the image to the dialog; `true` shows it at 1:1 and scrolls. */
  const [actualSize, setActualSize] = useState(false);

  /* A cached image can finish loading before React attaches `onLoad`, and then
     the event never fires. The ref runs on mount, so it settles that case. */
  const measure = useCallback((node: HTMLImageElement | null) => {
    if (node === null || !node.complete) {
      return;
    }
    setState(
      node.naturalWidth > 0
        ? { kind: "loaded", width: node.naturalWidth, height: node.naturalHeight }
        : { kind: "failed" },
    );
  }, []);

  const inline = (
    <img
      ref={measure}
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading="lazy"
      decoding="async"
      onLoad={(event) =>
        setState({
          kind: "loaded",
          width: event.currentTarget.naturalWidth,
          height: event.currentTarget.naturalHeight,
        })
      }
      onError={() => setState({ kind: "failed" })}
    />
  );

  const enlargeable =
    state.kind === "loaded" &&
    (state.width > INLINE_IMAGE_CAP_PX || state.height > INLINE_IMAGE_CAP_PX);

  if (!enlargeable) {
    return inline;
  }

  /* An empty `alt` is the norm: Ivanti's editor writes none. The control still
     needs a name, so it falls back to a generic one rather than to "". */
  const name = alt.trim();
  const label = name === "" ? t("image.enlarge") : t("image.enlargeNamed", { name });

  return (
    <Dialog.Root
      onOpenChange={(open) => {
        // Reopening always starts fitted, so nobody lands mid-pan on a 4000px
        // screenshot because they zoomed the last one.
        if (!open) {
          setActualSize(false);
        }
      }}
    >
      <Dialog.Trigger
        aria-label={label}
        className="focus-visible:ring-focus-ring inline-block max-w-full cursor-zoom-in rounded-md align-middle focus-visible:ring-2 focus-visible:outline-none"
      >
        {inline}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Backdrop />
        {/* Near-viewport so a wide screenshot is legible rather than a thumbnail
            in a letterbox; the DS caps both axes for small screens anyway. */}
        <Dialog.Popup
          size="lg"
          className="max-w-container-xl flex h-[calc(100dvh-var(--norican-space-8))] w-[calc(100vw-var(--norican-space-8))] flex-col overflow-hidden"
        >
          <Dialog.Header>
            <Dialog.Title className="min-w-0 truncate">
              {name === "" ? t("image.title") : name}
            </Dialog.Title>
            <Dialog.CloseButton label={t("a11y.closeDialog")} />
          </Dialog.Header>
          <Dialog.Body className="bg-bg-inset min-h-0 flex-1 overflow-auto">
            {/* Fit first: the whole screenshot at once, no panning. `Actual
                size` then trades that for 1:1 pixels, which is what makes
                small error text readable. The auto margins that centre the
                fitted image are dropped there — they would put the overflowing
                edges out of scroll reach — and `self-start` replaces the
                stretch suppression they were also providing. */}
            <img
              src={src}
              alt={alt}
              className={cn(
                "block rounded-md",
                actualSize ? "max-h-none max-w-none self-start" : "m-auto max-h-full max-w-full",
              )}
            />
          </Dialog.Body>
          <Dialog.Footer>
            <Button
              variant="outline"
              tone="neutral"
              startIcon={<Icon icon={actualSize ? Minimize2 : ZoomIn} size="sm" />}
              onClick={() => setActualSize((previous) => !previous)}
            >
              {actualSize ? t("image.fit") : t("image.actualSize")}
            </Button>
            <Dialog.Close
              render={
                <Button variant="solid" tone="accent">
                  {t("button.close")}
                </Button>
              }
            />
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Walking the inert tree ──────────────────────────────────────────────

function renderChildren(parent: Node, keyPrefix: string, depth: number): ReactNode[] {
  const children: ReactNode[] = [];
  parent.childNodes.forEach((child, index) => {
    const rendered = renderNode(child, `${keyPrefix}-${index}`, depth);
    if (rendered !== null) {
      children.push(rendered);
    }
  });
  return children;
}

function renderNode(node: Node, key: string, depth: number): ReactNode {
  if (node.nodeType === TEXT_NODE) {
    return node.nodeValue;
  }
  if (node.nodeType !== ELEMENT_NODE) {
    // Comments, processing instructions, doctypes: nothing to show.
    return null;
  }

  const element = node as Element;
  const tag = element.localName.toLowerCase();

  if (DROPPED_TAGS.has(tag)) {
    return null;
  }
  if (depth >= MAX_DEPTH) {
    // Deeper than any real ticket: keep the words, abandon the structure.
    return element.textContent;
  }

  const rendered = RENDERED_TAGS[tag];
  const children = VOID_TAGS.has(tag) ? [] : renderChildren(element, key, depth + 1);

  if (rendered === undefined) {
    // Not recognised and not dangerous — show what is inside it.
    return <Fragment key={key}>{children}</Fragment>;
  }

  switch (tag) {
    case "a": {
      const href = safeHref(element.getAttribute("href"));
      if (href === undefined) {
        // A link nobody may follow is still text worth reading.
        return <Fragment key={key}>{children}</Fragment>;
      }
      return (
        <a
          key={key}
          href={href}
          title={element.getAttribute("title") ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      );
    }

    case "img": {
      const src = safeImageSrc(element.getAttribute("src"));
      if (src === undefined) {
        // Say a picture was here rather than leave a silent gap.
        return <Fragment key={key}>{IMAGE_PLACEHOLDER}</Fragment>;
      }
      return (
        <TicketImage
          key={key}
          src={src}
          alt={element.getAttribute("alt") ?? ""}
          width={positiveInt(element, "width")}
          height={positiveInt(element, "height")}
        />
      );
    }

    case "td":
      return (
        <td
          key={key}
          colSpan={positiveInt(element, "colspan")}
          rowSpan={positiveInt(element, "rowspan")}
        >
          {children}
        </td>
      );

    case "th":
      return (
        <th
          key={key}
          colSpan={positiveInt(element, "colspan")}
          rowSpan={positiveInt(element, "rowspan")}
        >
          {children}
        </th>
      );

    default:
      return VOID_TAGS.has(tag)
        ? createElement(rendered, { key })
        : createElement(rendered, { key, className: mutedClass(element) }, children);
  }
}

/**
 * Parses in an inert document, then copies what is allowed into React.
 *
 * `DOMParser` is the browser's own HTML parser, so there is no parse
 * differential between what is inspected here and what would be rendered —
 * the class of bug that defeats every regex-based sanitiser.
 */
function renderHtml(html: string): ReactNode[] {
  const document = new DOMParser().parseFromString(html, "text/html");
  return renderChildren(document.body, "r", 0);
}

// ─── Presentation ────────────────────────────────────────────────────────

/**
 * Block styling for markup this app did not author.
 *
 * Preflight has flattened margins and list markers, so paragraphs, lists and
 * tables have to be given them back; descendant selectors do that without
 * touching the elements themselves, which keeps the renderer above free of
 * per-tag classes.
 *
 * The image rule is the load-bearing one. A single embedded screenshot can be
 * a 1920px-wide PNG carried as a third of a megabyte of base64, and these
 * entries appear in a timeline several at a time: unconstrained, one of them
 * sets the width of its column and pushes everything else off-screen. Capping
 * both axes with `w-auto`/`h-auto` leaves the browser's replaced-element
 * sizing to preserve the aspect ratio.
 *
 * The cap is right for the timeline and wrong for the screenshot: at 20rem the
 * error text nobody wanted to retype is unreadable. So it is a thumbnail with
 * a way out rather than a dead end — see {@link TicketImage}, which is what
 * `img` renders as, and {@link INLINE_IMAGE_CAP_PX}, which mirrors this number.
 */
const RICH_TEXT_CLASSES = cn(
  "break-words",
  "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
  "[&_p]:my-2",
  "[&_h3]:mt-3 [&_h3]:mb-1 [&_h4]:mt-3 [&_h4]:mb-1 [&_h5]:mt-3 [&_h5]:mb-1 [&_h6]:mt-3 [&_h6]:mb-1",
  "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5",
  "[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5",
  "[&_li]:my-1",
  "[&_blockquote]:border-border-strong [&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:pl-3",
  "[&_pre]:bg-bg-inset [&_pre]:border-border-subtle [&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:border [&_pre]:p-3",
  "[&_code]:font-mono",
  "[&_a]:text-accent-fg-subtle [&_a]:underline [&_a]:underline-offset-2",
  "[&_hr]:border-border-subtle [&_hr]:my-3",
  "[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse",
  "[&_td]:border-border-subtle [&_td]:border [&_td]:px-2 [&_td]:py-1",
  "[&_th]:border-border-subtle [&_th]:border [&_th]:px-2 [&_th]:py-1 [&_th]:text-left",
  "[&_img]:border-border-subtle [&_img]:my-2 [&_img]:h-auto [&_img]:max-h-container-xs [&_img]:w-auto [&_img]:max-w-full [&_img]:rounded-md [&_img]:border",
);

export interface RichTextProps {
  /** Markup already sanitised by the backend. Never a raw provider string. */
  html: string;
  /** BCP-47 tag for the rendered content. */
  lang?: string;
  className?: string;
}

/**
 * Renders sanitised provider markup.
 *
 * Memoised on the source string: the description of the UAT incident that
 * prompted all this is 333 kB, and re-walking it on every unrelated re-render
 * of a ticket page would be the one place this surface could get slow.
 */
export function RichText({ html, lang, className }: RichTextProps) {
  const content = useMemo(() => renderHtml(html), [html]);

  if (html.trim() === "") {
    return null;
  }

  return (
    <div className={cn(RICH_TEXT_CLASSES, className)} lang={lang}>
      {content}
    </div>
  );
}
